<script lang="ts">
	import type { PartForClient as Part, PartStoreLocations } from 'utility/load-part'
	import type { i18n } from 'types/common'

	import Autocomplete from '@isoftdata/svelte-autocomplete'
	import Button from '@isoftdata/svelte-button'
	import Checkbox from '@isoftdata/svelte-checkbox'
	import Icon from '@isoftdata/svelte-icon'
	import Input from '@isoftdata/svelte-input'
	import Table, { DraggableRows, Td, type Column } from '@isoftdata/svelte-table'

	import { tick } from 'svelte'
	import { v4 as uuid } from '@lukeed/uuid'
	import { hasPermission } from 'utility/permission'
	import { getEventValue, getEventChecked } from '@isoftdata/browser-event'
	import { klona } from 'klona'
	import { dequal } from 'dequal'
	import { getContext } from 'svelte'

	interface Props {
		partQuantity: number
		locations: Part['locations']
		serialized: boolean
		allowVirtualLocations: boolean
		partStoreLocations: PartStoreLocations
		varianceLocationName: string
		storeId: number
	}

	let {
		partQuantity,
		locations = $bindable(),
		serialized,
		allowVirtualLocations,
		partStoreLocations,
		varianceLocationName,
		storeId,
	}: Props = $props()

	let table: Table<(typeof locations)[number]> = $state()!

	const { t: translate } = getContext<i18n>('i18next')

	const origLocations: typeof locations = klona(locations)

	const columns: Array<Column<(typeof locations)[number]>> = [
		{
			name: translate('common:rank', 'Rank'),
			property: 'rank',
			width: '1rem',
			align: 'center',
			defaultSortColumn: true,
			defaultSortDirection: 'ASC',
		},
		{
			name: '',
			property: '_ACTION',
			icon: 'info-circle',
			align: 'center',
			sortType: false,
			width: '1rem',
		},
		{
			property: 'name',
			name: translate('common:location', 'Location'),
			sortType: false,
			minWidth: '150px',
			width: '50%',
			title: translate('part.theLocationOfThisItem', 'The location of this item'),
		},
		{
			property: 'permanent',
			name: translate('common:permanent', 'Permanent'),
			sortType: false,
			width: '1rem',
			title: translate(
				'part.permanentLocationsDonTGetRemovedFromThePartWhenTheQuantityReaches0',
				"Permanent locations don't get removed from the part when the quantity reaches 0",
			),
		},
		{
			property: 'quantity',
			name: translate('common:quantity', 'Quantity'),
			numeric: true,
			sortType: false,
			width: '1rem',
			title: translate('part.theQuantityAtThisParticularLocation', 'The quantity at this particular location'),
		},
		{
			property: '_availableQuantity',
			name: translate('common:statusavailable', 'Available'),
			numeric: true,
			width: '1rem',
			title: translate('part.theQuantityAvailableAtThisLocation', 'The quantity available at this location'),
		},
		{
			property: 'holdQuantity',
			name: translate('common:held', 'Held'),
			numeric: true,
			sortType: false,
			width: '1rem',
			title: translate('part.theQuantityReservedAtThisLocation', 'The quantity reserved at this location'),
		},
		{
			name: '',
			property: '_REMOVE',
			icon: 'trash',
			align: 'center',
			sortType: false,
			width: '1rem',
			title: translate(
				'part.markThisPartForDeletionItWillBeDeletedOnSave',
				'Mark this part for deletion. It will be deleted on save',
			),
		},
	]

	// will be called by serial table so we have to export it
	export function addLocation(name = '', locationId: number | null = null, rank?: number) {
		locations.push(newLocation(name, locationId, rank))
		locations = locations
		recalculateVariance()
	}

	export function normalizeRanks() {
		locations
			.sort((a, b) => (a.rank ?? 1) - (b.rank ?? 1))
			.forEach((location, index) => {
				location.rank = index + 1
			})
	}

	function newLocation(name = '', locationId: number | null = null, rank?: number) {
		const location = partStoreLocations.find(location => location.name === name)
		return {
			id: null,
			locationId,
			name,
			description: location?.description ?? '',
			allowInventory: true,
			deleted: false,
			quantity: 1,
			holdQuantity: 0,
			permanent: false,
			rank: rank ?? locations.length + 1,
			uuid: uuid(),
		}
	}

	function getQuantityAtAllLocations(locations: Part['locations']) {
		return locations.reduce((sum, location) => {
			if (location.name === varianceLocationName || location.deleted) {
				return sum
			}
			return sum + location.quantity
		}, 0)
	}

	function recalculateVariance() {
		const newQuantityAtVariance = partQuantity - getQuantityAtAllLocations(locations)

		if (newQuantityAtVariance && newQuantityAtVariance !== quantityAtVariance) {
			const varianceIndex = locations.findIndex(location => location.name === varianceLocationName)
			if (varianceIndex > -1) {
				locations[varianceIndex].quantity = newQuantityAtVariance
			} else {
				const newVarianceLocation = newLocation(varianceLocationName)
				newVarianceLocation.quantity = newQuantityAtVariance
				locations.push(newVarianceLocation)
			}
		} else if (newQuantityAtVariance === 0) {
			const varianceIndex = locations.findIndex(location => location.name === varianceLocationName)
			if (varianceIndex > -1 && !locations[varianceIndex].permanent) {
				locations.splice(varianceIndex, 1)
			} else if (varianceIndex > -1) {
				locations[varianceIndex].quantity = 0
			}
		}
		locations = locations
	}

	let quantityAtAllLocations = $derived(getQuantityAtAllLocations(locations))
	let quantityAtVariance = $derived(quantityAtAllLocations - partQuantity)
</script>

<Table
	responsive
	{columns}
	parentClass="mb-1"
	tableId="locationTable"
	idProp="uuid"
	rowSelectionIdProp="uuid"
	rows={locations}
	bind:this={table}
>
	{#snippet body({ rows: currentPageRows, visibleColumnsCount })}
		<DraggableRows
			showRank
			{table}
			{columns}
			{currentPageRows}
			dataType="application/location-table-row"
			rankProperty="rank"
			bind:rows={locations}
			computeClass={row => (row.deleted ? 'table-danger' : '')}
		>
			{#snippet children({ row })}
				{@const isVariance = row.name === varianceLocationName}
				<Td property="_ACTION">
					{#if row.deleted}
						<Icon
							icon="trash"
							class="text-danger"
						/>
						<!-- 
						* locations[row.originalIndex] could be undefined as we splice it out if it is a newly added location before saving;
						* if is variance or serialized location, the fields would be all disabled as they are being automated and cannot be change manually, so I think there's no need to show the indicator;
						* if id is null, which means it is a newly added location, we show the modified indicator
						* if there's changes on the location, comparing it to previous value, show the modified indicator
					-->
					{:else if locations[row.originalIndex] && !(isVariance || serialized) && (locations[row.originalIndex].id === null || !dequal(locations[row.originalIndex], origLocations[row.originalIndex]))}
						<Icon
							icon="pencil"
							class="text-warning"
						/>
					{/if}
				</Td>
				<Td
					property="name"
					title={row.description}
				>
					<Autocomplete
						showLabel={false}
						id="location-autocomplete-{row.originalIndex}"
						labelParentClass="m-0"
						placeholder={allowVirtualLocations
							? translate('part.selectOrCreateALocation', 'Select or create a location')
							: translate('part.selectALocation', 'Select a location')}
						options={partStoreLocations}
						canAddNew={allowVirtualLocations}
						disabled={row.deleted ||
							isVariance ||
							serialized ||
							!hasPermission('PARTS_CAN_EDIT_LOCATION', storeId) ||
							!hasPermission('PARTS_CAN_EDIT_INFO', storeId)}
						value={row}
						createOption={name => newLocation(name)}
						getLabel={location => location.name}
						change={async newLocation => {
							const oldLocation = locations[row.originalIndex]

							locations[row.originalIndex] = {
								...oldLocation,
								locationId: newLocation?.id || null,
								name: newLocation?.name,
								description: newLocation?.description || null,
								id: null,
								uuid: uuid(),
							}
							if (oldLocation.quantity && oldLocation.id) {
								// Keep the old location, but set its quantity to 0 so it gets saved as such
								locations.push({
									...oldLocation,
									quantity: 0,
								})
							}

							locations = locations
							await tick()
							const input = document.getElementById(`location-autocomplete-${row.originalIndex}`)
							if (input && input instanceof HTMLInputElement) {
								input.focus()
							}
						}}
					/>
				</Td>
				<Td
					property="permanent"
					class="text-center"
					title={translate(
						'part.permanentLocationsDonTGetRemovedFromThePartWhenTheQuantityReaches0',
						"Permanent locations don't get removed from the part when the quantity reaches 0",
					)}
				>
					<Checkbox
						showLabel={false}
						disabled={row.deleted ||
							serialized ||
							!hasPermission('PARTS_CAN_EDIT_LOCATION', storeId) ||
							!hasPermission('PARTS_CAN_EDIT_INFO', storeId)}
						checked={row.permanent}
						onchange={event => {
							locations[row.originalIndex].permanent = getEventChecked(event) ?? false
							recalculateVariance()
						}}
					/>
				</Td>
				<Td property="quantity">
					<Input
						showLabel={false}
						id="quantityInput"
						class="text-end"
						type="number"
						labelParentClass="m-0"
						disabled={row.deleted ||
							isVariance ||
							serialized ||
							!hasPermission('PARTS_CAN_EDIT_LOCATION', storeId) ||
							!hasPermission('PARTS_CAN_EDIT_INFO', storeId)}
						value={row.quantity}
						onchange={event => {
							locations[row.originalIndex].quantity = parseInt(getEventValue(event) ?? '0', 10)
							recalculateVariance()
						}}
					/>
				</Td>
				<Td
					property="_availableQuantity"
					class="text-end">{row.quantity - row.holdQuantity}</Td
				>
				<Td
					property="holdQuantity"
					class="text-end">{row.holdQuantity}</Td
				>
				<Td property="_REMOVE">
					<div class="d-flex justify-content-center">
						<Button
							size="sm"
							color="danger"
							outline={!row.deleted}
							disabled={isVariance ||
								serialized ||
								!hasPermission('PARTS_CAN_EDIT_LOCATION', storeId) ||
								!hasPermission('PARTS_CAN_EDIT_INFO', storeId)}
							iconClass={row.deleted ? 'trash-undo' : 'trash'}
							title={isVariance
								? translate(
										'part.toRemoveVarianceAdjustQuantityAtOtherLocations',
										'To remove variance, adjust quantity at other locations.',
									)
								: row.deleted
									? translate('part.undoDelete', 'Undo delete')
									: translate(
											'part.markThisPartForDeletionItWillBeDeletedOnSave',
											'Mark this part for deletion. It will be deleted on save',
										)}
							onclick={() => {
								if (locations[row.originalIndex].id) {
									locations[row.originalIndex].deleted = !row.deleted
								} else {
									locations = locations.toSpliced(row.originalIndex, 1)
								}
								recalculateVariance()
							}}
						/>
					</div>
				</Td>
			{/snippet}
		</DraggableRows>
		{#if !locations.length}
			<tr>
				<td
					class="text-center"
					colspan={visibleColumnsCount}
					>{#if serialized}
						{translate(
							'part.noLocationsSelectALocationForEachSerialForThisPart',
							'No locations, select a location for each serial # for this part',
						)}{:else}
						{translate(
							'part.noLocationsClickNewLocationToAddANewOne',
							'No locations, click "New Location" to add a new one',
						)}{/if}</td
				>
			</tr>
		{/if}
	{/snippet}
</Table>

{#if !serialized && hasPermission('PARTS_CAN_EDIT_LOCATION', storeId)}
	<div>
		<Button
			outline
			size="sm"
			color="success"
			iconClass="plus"
			disabled={serialized ||
				!hasPermission('PARTS_CAN_EDIT_LOCATION', storeId) ||
				!hasPermission('PARTS_CAN_EDIT_INFO', storeId)}
			onclick={() => addLocation()}>{translate('part.newLocation', 'New Location')}</Button
		>
	</div>
{/if}
